import { arrayUpdateInPlace, moveToFulfilled, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { AddGroupLeaderFulfilledAction } from '../../domains/groupAddRemoveLeader/action.types';
import type {
  GroupAddUserFulfilledAction,
  GroupRemoveUserFulfilledAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import type {
  GroupMakeAdminFulfilledAction,
  GroupRevokeAdminFulfilledAction,
} from '../../domains/groupMakeRevokeAdmin/actions.types';
import type { ResolveGroupMembershipFromMembershipPanelFulfilledAction } from '../../domains/groupResolveMembershipRequest/actions.types';
import type { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import type { Group, GroupMembersEdge, User } from '../types';

import type { GroupSidebarMembers, GroupsSidebarMembers } from './types';

export type GroupsMembersAction =
  | GroupSidebarLoadFulfilledAction
  | ResolveGroupMembershipFromMembershipPanelFulfilledAction
  | GroupAddUserFulfilledAction
  | GroupRemoveUserFulfilledAction
  | GroupMakeAdminFulfilledAction
  | GroupRevokeAdminFulfilledAction
  | AddGroupLeaderFulfilledAction;

const initialState: GroupsSidebarMembers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithUpdatedFulfilledGroupMembers = (
  state: GroupsSidebarMembers,
  id: Group['id'],
  updatedFields: Partial<GroupSidebarMembers>
) => update(state, id, updatedFields);

const filterMembersNotIncludingUser = (edges: GroupMembersEdge[], userId: User['id']) =>
  edges.filter((edge) => edge.nodeId !== userId);

const memberEdgeCompare = (a: GroupMembersEdge, b: GroupMembersEdge) => a.nodeId === b.nodeId;

const makeMemberGroupAdmin = (
  state: GroupsSidebarMembers,
  { groupId, userId }: GroupMakeAdminFulfilledAction['payload']
) => {
  const groupSidebarMembers = state.fulfilled.byId[groupId];
  if (!groupSidebarMembers) {
    return state;
  }

  const { featuredMemberEdges, pendingMemberEdges } = groupSidebarMembers;
  const newGroupAdmin: GroupMembersEdge = {
    isAdmin: true,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupSidebarMembers,
    featuredMemberEdges: arrayUpdateInPlace<GroupMembersEdge>(featuredMemberEdges, newGroupAdmin, memberEdgeCompare),
    pendingMemberEdges: filterMembersNotIncludingUser(pendingMemberEdges, userId),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const revokeMembersGroupAdminStatus = (
  state: GroupsSidebarMembers,
  { groupId, userId }: GroupMakeAdminFulfilledAction['payload']
) => {
  const groupSidebarMembers = state.fulfilled.byId[groupId];
  if (!groupSidebarMembers) {
    return state;
  }

  // oxlint-disable-next-line typescript/no-useless-default-assignment -- Communities team will need to fix this
  const { featuredMemberEdges, pendingMemberEdges = [] } = groupSidebarMembers;
  const newGroupMember: GroupMembersEdge = {
    isAdmin: false,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupSidebarMembers,
    featuredMemberEdges: arrayUpdateInPlace<GroupMembersEdge>(featuredMemberEdges, newGroupMember, memberEdgeCompare),
    pendingMemberEdges: [...filterMembersNotIncludingUser(pendingMemberEdges, userId)],
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const addMemberToGroup = (state: GroupsSidebarMembers, { groupId, userId }: GroupAddUserFulfilledAction['payload']) => {
  const groupSidebarMembers = state.fulfilled.byId[groupId];

  if (!groupSidebarMembers) {
    return state;
  }

  const { featuredMemberEdges, pendingMemberEdges } = groupSidebarMembers;
  const newGroupMember: GroupMembersEdge = {
    isAdmin: false,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupSidebarMembers,
    featuredMemberEdges: [...featuredMemberEdges, newGroupMember],
    pendingMemberEdges: [...filterMembersNotIncludingUser(pendingMemberEdges, userId)],
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const removeMemberFromGroup = (
  state: GroupsSidebarMembers,
  { groupId, userId }: GroupRemoveUserFulfilledAction['payload']
) => {
  const groupSidebarMembers = state.fulfilled.byId[groupId];
  if (!groupSidebarMembers) {
    return state;
  }

  const { featuredMemberEdges, pendingMemberEdges } = groupSidebarMembers;

  const updatedGroupMembers = {
    ...groupSidebarMembers,
    featuredMemberEdges: filterMembersNotIncludingUser(featuredMemberEdges, userId),
    pendingMemberEdges: filterMembersNotIncludingUser(pendingMemberEdges, userId),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const handleFulfilledMembershipRequest = (
  state: GroupsSidebarMembers,
  payload: ResolveGroupMembershipFromMembershipPanelFulfilledAction['payload']
) => {
  const membershipRequestType = payload.requestType;

  return membershipRequestType === 'approve' ? addMemberToGroup(state, payload) : removeMemberFromGroup(state, payload);
};

const reducer: Reducer<GroupsSidebarMembers, GroupsMembersAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupSidebarMembers);
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_FULFILLED':
      return handleFulfilledMembershipRequest(state, action.payload);
    case 'GROUP.ADD_USER_FULFILLED':
      return addMemberToGroup(state, action.payload);
    case 'GROUP.REMOVE_USER_FULFILLED':
      return removeMemberFromGroup(state, action.payload);
    case 'GROUP.MAKE_ADMIN_FULFILLED':
    case 'GROUP.ADD_LEADER_FULFILLED':
      return makeMemberGroupAdmin(state, action.payload);
    case 'GROUP.REVOKE_ADMIN_FULFILLED':
      return revokeMembersGroupAdminStatus(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
